import type { BuildLookupRow, MemberDemographics } from "../types";
import { ageAsOf, isJuvenile } from "./age";

export type { BuildLookupRow } from "../types";

export const MIN_REALISTIC_HEIGHT_INCHES = 12;
export const MAX_REALISTIC_HEIGHT_INCHES = 7 * 12 + 11;
export const MIN_REALISTIC_WEIGHT_LBS = 2;
/** Hard cap (feedback 4.3): the form and the API refuse more; the build table stops well before it. */
export const MAX_WEIGHT_LBS = 999;
export const JUVENILE_HEIGHT_NOT_FOUND_POINTS = 5;

export function totalHeightInches(feet: number | null, inches: number | null): number | null {
  if (feet === null) return null;
  return feet * 12 + (inches ?? 0);
}

/** Soft warning thresholds (product plan 4.2): outside 1'0" to 7'11". Missing height is not unrealistic. */
export function isUnrealisticHeight(feet: number | null, inches: number | null): boolean {
  const total = totalHeightInches(feet, inches);
  if (total === null) return false;
  return total < MIN_REALISTIC_HEIGHT_INCHES || total > MAX_REALISTIC_HEIGHT_INCHES;
}

/** Soft warning threshold (product plan 4.2): under 2 lbs. The upper bound is the hard cap, MAX_WEIGHT_LBS. */
export function isUnrealisticWeight(lbs: number | null): boolean {
  if (lbs === null) return false;
  return lbs < MIN_REALISTIC_WEIGHT_LBS;
}

function rowsForHeight(table: readonly BuildLookupRow[], juvenile: boolean, gender: "M" | "F", heightInches: number): BuildLookupRow[] {
  const heightRows = table.filter((row) => row.isJuvenile === juvenile && row.heightInchesTotal === heightInches);
  const genderSpecific = heightRows.filter((row) => row.gender === gender);
  if (genderSpecific.length > 0) return genderSpecific;
  return heightRows.filter((row) => row.gender === null);
}

/**
 * Build points from the lookup table (product plan 4.2).
 * Missing gender, DOB, height, or weight gives 0. A juvenile height not in the
 * table gives 5. Weights outside the table take the boundary row's points plus
 * one point per pound of difference.
 */
export function buildPoints(member: MemberDemographics, effectiveDate: string, table: readonly BuildLookupRow[]): number {
  const heightInches = totalHeightInches(member.heightFeet, member.heightInches);
  if (member.gender === null || member.dateOfBirth === null || heightInches === null || member.weightLbs === null) return 0;

  const age = ageAsOf(member.dateOfBirth, effectiveDate);
  if (age === null) return 0;
  const juvenile = isJuvenile(age);
  const rows = rowsForHeight(table, juvenile, member.gender, heightInches);
  if (rows.length === 0) return juvenile ? JUVENILE_HEIGHT_NOT_FOUND_POINTS : 0;

  const weight = member.weightLbs;
  const exact = rows.find((row) => weight >= row.weightMin && weight <= row.weightMax);
  if (exact) return exact.points;

  const lowest = rows.reduce((min, row) => (row.weightMin < min.weightMin ? row : min));
  const highest = rows.reduce((max, row) => (row.weightMax > max.weightMax ? row : max));
  if (weight > highest.weightMax) return highest.points + (weight - highest.weightMax);
  return lowest.points + (lowest.weightMin - weight);
}
